import * as THREE from 'three';
import { glslFloat } from './shaders/common.glsl';

/**
 * Distant planets and moons as points of light.
 *
 * `AstronomicalLayer` already draws every body as a shaded impostor sphere at a
 * compressed distance, and that sphere is geometrically correct at every range:
 * angular size is preserved exactly. Correct is not the same as visible. A
 * planet at real interplanetary distance subtends tens of microradians — a
 * fraction of one pixel — so the impostor collapses to a single dim, partially
 * covered fragment and simply vanishes against a starfield or a twilight sky.
 * Venus and Jupiter are unmistakable to the naked eye for a reason that has
 * nothing to do with their angular size: they are *bright*, and a real eye (or
 * lens) spreads a bright point across several pixels' worth of point-spread
 * function. That spread is what this draws.
 *
 * This is the same trick `SunGlare` plays for the star, at a thousandth of the
 * scale and driven by an actual photometric estimate rather than by bloom: see
 * `AstronomicalLayer.update`, which computes each body's received flux from its
 * albedo, radius, distance from the star, phase angle and distance to the
 * camera, and hands the result here as a size and an intensity.
 *
 * One `THREE.Points` object for the whole system, rebuilt in place each frame —
 * no per-body object, no allocation in the hot path, one draw call. It lives in
 * `AstronomicalLayer.root`, so it renders in the astronomical pass alongside the
 * impostors it stands in for (and is covered by the same `debugLayers` switch).
 *
 * Deliberately NOT depth tested, for `SunGlare`'s reason: a point spread is an
 * artefact of the optics, formed after the image, and the point sits at its own
 * impostor's *centre* — depth testing it would put it behind that impostor's
 * own front surface and hide every one of them. The world passes still run
 * after this one with their depth cleared, so terrain never lets a planet shine
 * through the ground; the only thing this gives up is one astronomical impostor
 * occulting another, which needs two bodies aligned to within a pixel.
 */

/** Shapes the saturated core of the point spread. Higher is tighter. */
const CORE_FALLOFF_POWER = 3.2;
/** Shapes the faint skirt around it, the part that makes a bright body read as bright rather than merely present. */
const HALO_FALLOFF_POWER = 1.1;
const HALO_STRENGTH = 0.22;

const VERTEX = /* glsl */ `
attribute float aSize;
attribute float aIntensity;
varying vec3 vColor;
varying float vIntensity;
uniform float uPixelRatio;
void main() {
  vColor = color;
  vIntensity = aIntensity;
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.0);
  gl_PointSize = aSize * uPixelRatio;
}
`;

const FRAGMENT = /* glsl */ `
precision highp float;

varying vec3 vColor;
varying float vIntensity;
uniform float uBrightness;

void main() {
  vec2 d = gl_PointCoord - vec2(0.5);
  float r = length(d) * 2.0;
  if (r > 1.0) discard;
  float falloff = max(0.0, 1.0 - r);
  // Core plus skirt, the same two-term shape 'SunGlare' uses for the star: a
  // single power falls off too fast and reads as a hard little disc.
  float profile = pow(falloff, ${glslFloat(CORE_FALLOFF_POWER)})
                + pow(falloff, ${glslFloat(HALO_FALLOFF_POWER)}) * ${glslFloat(HALO_STRENGTH)};
  gl_FragColor = vec4(vColor * vIntensity * uBrightness * profile, profile);

  // See TerrainMaterial. These points sit next to the starfield, which also
  // encodes; an unencoded one would drift away from it in colour and exposure.
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export class DistantBodyPoints {
  readonly object: THREE.Points;
  private readonly geometry = new THREE.BufferGeometry();
  private readonly material: THREE.ShaderMaterial;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly sizes: Float32Array;
  private readonly intensities: Float32Array;
  private count = 0;

  constructor(capacity: number) {
    this.positions = new Float32Array(capacity * 3);
    this.colors = new Float32Array(capacity * 3);
    this.sizes = new Float32Array(capacity);
    this.intensities = new Float32Array(capacity);

    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));
    this.geometry.setAttribute('aSize', new THREE.BufferAttribute(this.sizes, 1));
    this.geometry.setAttribute('aIntensity', new THREE.BufferAttribute(this.intensities, 1));
    this.geometry.setDrawRange(0, 0);

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uPixelRatio: { value: 1 },
        uBrightness: { value: 1 },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });

    this.object = new THREE.Points(this.geometry, this.material);
    this.object.frustumCulled = false;
    // After the impostor spheres (0) and their rings (1), so a point always
    // lies over the disc it stands in for rather than under it.
    this.object.renderOrder = 10;
  }

  /** Starts a frame. Every visible body must be re-`add`ed before `commit`. */
  begin(): void {
    this.count = 0;
  }

  /**
   * @param sizePixels point-sprite diameter in device-independent pixels
   * @param intensity radiance multiplier; 0 draws nothing worth drawing
   */
  add(
    x: number,
    y: number,
    z: number,
    r: number,
    g: number,
    b: number,
    sizePixels: number,
    intensity: number,
  ): void {
    const i = this.count;
    if (i * 3 + 2 >= this.positions.length) return;
    this.positions[i * 3] = x;
    this.positions[i * 3 + 1] = y;
    this.positions[i * 3 + 2] = z;
    this.colors[i * 3] = r;
    this.colors[i * 3 + 1] = g;
    this.colors[i * 3 + 2] = b;
    this.sizes[i] = sizePixels;
    this.intensities[i] = intensity;
    this.count = i + 1;
  }

  /** Uploads this frame's points. */
  commit(): void {
    this.geometry.setDrawRange(0, this.count);
    if (this.count === 0) return;
    for (const name of ['position', 'color', 'aSize', 'aIntensity']) {
      this.geometry.getAttribute(name).needsUpdate = true;
    }
  }

  setPixelRatio(ratio: number): void {
    this.material.uniforms.uPixelRatio.value = ratio;
  }

  /**
   * Same atmospheric washout the starfield gets (`Starfield.setAtmosphericWashout`).
   * A planet is a point source in exactly the way a star is, so the daylit sky
   * has to swallow it for the same reason and by the same amount — otherwise
   * every world in the system hangs visibly in a noon sky.
   */
  setWashout(brightness: number): void {
    this.material.uniforms.uBrightness.value = brightness;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
